import Decimal from "decimal.js";
import { z } from "zod";

const requiredReason = z.string().trim().min(1, "A reason is required");

export const SendOtcInvoiceQueueEntriesRequestValidation = z.object({
  queue_entry_ids: z.array(z.string().uuid()).min(1),
});

export const SkipOtcInvoiceQueueEntriesRequestValidation = z.object({
  items: z.array(z.object({
    queue_entry_id: z.string().uuid(),
    reason: requiredReason,
  })).min(1),
});

export const ArchiveOtcInvoicesRequestValidation = z.object({
  items: z.array(z.object({
    invoice_id: z.string().uuid(),
    reason: requiredReason,
  })).min(1),
});

export const DownloadSentOtcInvoiceRequestValidation = z.object({
  invoice_id: z.string().uuid(),
  document_type: z.enum(["excel", "pdf"]),
});

/**
 * Portal dates are timezone-less calendar days: the backend converts
 * `YYYY-MM-DD` into midnight in `CommonDestinationTimezone`, which is how
 * `OtcInvoice.invoice_date` is stored.
 */
const portalDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be formatted as YYYY-MM-DD");

export const ExportSentOtcInvoicesReportRequestValidation = z
  .object({
    start_date: portalDate,
    end_date: portalDate,
  })
  .refine(
    (value: { start_date: string; end_date: string }): boolean => value.start_date <= value.end_date,
    { message: "Start date must be on or before end date", path: ["end_date"] },
  );

const OtcInvoiceAllocationInputValidation = z.object({
  id: z.string().uuid().optional(),
  customer_id: z.string().uuid(),
  percentage: z.string().trim().min(1).refine((value: string): boolean => {
    try {
      const percentage = new Decimal(value);
      return percentage.isFinite() && percentage.isPositive();
    } catch {
      return false;
    }
  }, "Percentage must be a positive decimal"),
});

export const UpdateOtcInvoiceAllocationsRequestValidation = z.object({
  queue_entry_id: z.string().uuid(),
  allocations: z.array(OtcInvoiceAllocationInputValidation).min(1),
}).superRefine((value, context: z.RefinementCtx): void => {
  const customerIds = value.allocations.map((allocation) => allocation.customer_id);
  if (new Set(customerIds).size !== customerIds.length) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: "Allocation customers must be unique", path: ["allocations"] });
  }

  try {
    const total = value.allocations.reduce(
      (sum: Decimal, allocation): Decimal => sum.plus(allocation.percentage),
      new Decimal(0),
    );
    if (!total.eq(100)) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: "Allocation percentages must total exactly 100", path: ["allocations"] });
    }
  } catch {
    context.addIssue({ code: z.ZodIssueCode.custom, message: "Allocation percentages must be valid decimals", path: ["allocations"] });
  }
});

export type SendOtcInvoiceQueueEntriesRequest = z.infer<typeof SendOtcInvoiceQueueEntriesRequestValidation>;
export type SkipOtcInvoiceQueueEntriesRequest = z.infer<typeof SkipOtcInvoiceQueueEntriesRequestValidation>;
export type ArchiveOtcInvoicesRequest = z.infer<typeof ArchiveOtcInvoicesRequestValidation>;
export type DownloadSentOtcInvoiceRequest = z.infer<typeof DownloadSentOtcInvoiceRequestValidation>;
export type ExportSentOtcInvoicesReportRequest = z.infer<typeof ExportSentOtcInvoicesReportRequestValidation>;
export type UpdateOtcInvoiceAllocationsRequest = z.infer<typeof UpdateOtcInvoiceAllocationsRequestValidation>;
